import { ref, inject } from 'vue';

export function useCustomerUtils() {
    const backendApi = inject('backendApi');
    const loading = ref(false);

    const listCustomerAddress = async (customerId = null) => {
        try {
            const response = await backendApi().customers().searchAddress({
                page: 1,
                limit: 1000,
                customerId: customerId
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching customer addresses:', error);
            return [];
        }
    };

    const getCustomer = async (customerId) => {
        try {
            const response = await backendApi().customers().get(customerId);
            return response;
        } catch (err) {
            return null;
        }
    };

    const getCustomerAddress = async (addressId) => {
        try {
            const response = await backendApi()
                .customers()
                .getAddress(addressId);
            return response.data;
        } catch (err) {
            return null;
        }
    };

    const getCustomerAddresses = async (customerId) => {
        try {
            const response = await backendApi().customers().searchAddress({
                page: 1,
                limit: 1000,
                customerId: customerId
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching customer addresses:', error);
            return [];
        }
    };

    const createAddress = async (addressData) => {
        try {
            const response = await backendApi()
                .customers()
                .createAddress(addressData);
            return response.data;
        } catch (error) {
            throw new Error(`Error creating address: ${error.message}`);
        }
    };

    const updateAddress = async (addressId, addressData) => {
        try {
            const response = await backendApi()
                .customers()
                .updateAddress({
                    id: addressId,
                    ...addressData
                });
            return response.data;
        } catch (error) {
            throw new Error(`Error updating address: ${error.message}`);
        }
    };

    const createCustomer = async (customerData) => {
        try {
            const response = await backendApi()
                .customers()
                .create(customerData);
            return response.data;
        } catch (error) {
            throw new Error(`Error creating customer: ${error.message}`);
        }
    };

    const updateCustomer = async (customerId, customerData) => {
        try {
            const response = await backendApi()
                .customers()
                .update({
                    id: customerId,
                    ...customerData
                });
            return response.data;
        } catch (error) {
            throw new Error(`Error updating customer: ${error.message}`);
        }
    };

    const saveCustomerWithAddresses = async (customerId, data) => {
        try {
            let customerResponse;

            if (customerId) {
                customerResponse = await updateCustomer(
                    customerId,
                    data.customer
                );
            } else {
                customerResponse = await createCustomer(data.customer);
            }

            if (data.addresses && data.addresses.length > 0) {
                const addressPromises = data.addresses.map((address) => {
                    address.customerId = customerResponse.id;

                    if (address.id) {
                        return updateAddress(address.id, address);
                    } else {
                        return createAddress(address);
                    }
                });

                await Promise.all(addressPromises);
            }

            return customerResponse;
        } catch (error) {
            throw new Error(`Error saving customer: ${error.message}`);
        }
    };

    const removeCustomer = async (id) => {
        loading.value = true;
        try {
            await backendApi().customers().remove(id);
            loading.value = false;
            return true;
        } catch (err) {
            loading.value = false;
            return false;
        }
    };

    const createRequestBody = (options) => {
        let payload = {
            agency: options.agency?.id,
            name: options.name,
            lastName: options.lastname,
            docid: options.docid,
            phone: options.phone,
            mobile: options.mobile,
            email: options.email,
            agent: options.agent?.id,
            location: options.location?.id,
            customerType: options.customerType === true ? 2 : 1,
            groupTariff: options.groupTariff?.id,
            address: options.address,
            city: options.city?.id,
            zip: options.zip,
            emailAddresses: options.emailAddresses,
            id: options?.id
        };

        return payload;
    };

    const saveCustomer = async (options) => {
        const payload = createRequestBody(options);
        try {
            const response = payload.id
                ? await backendApi().customers().update(payload)
                : await backendApi().customers().create(payload);
            return {
                success: true,
                data: response.data
            };
        } catch (err) {
            return {
                success: false,
                data: err.data
            };
        }
    };

    const saveCustomerAddress = async (payload) => {
        try {
            const response = await backendApi().customers().createAddress({
                customerId: payload.customerId,
                agency: payload.agency?.id,
                name: payload.name,
                lastName: payload.lastname,
                address: payload.address,
                city: payload.city?.id,
                zip: payload.zip,
                email: payload.email,
                phone: payload.phone,
                mobile: payload.mobile,
                docid: payload.docid,
                emailAddresses: payload.emailAddresses
            });
            return {
                success: true,
                data: response.data
            };
        } catch (err) {
            return {
                success: false,
                data: err
            };
        }
    };

    const updateCustomerAddress = async (options) => {
        const createRequestBody = (options) => {
            return {
                id: options.id,
                name: options.name,
                lastName: options.lastname,
                address: options.address,
                city: options.city?.id,
                zip: options.zip,
                email: options.email,
                phone: options.phone,
                mobile: options.mobile,
                docid: options.docid,
                emailAddresses: options.emailAddresses
            };
        };

        try {
            const payload = createRequestBody(options);
            const response = await backendApi()
                .customers()
                .updateAddress(payload);
            return {
                success: true,
                data: response.data
            };
        } catch (err) {
            return {
                success: false,
                data: null
            };
        }
    };

    const setFieldModelsGeneralData = (customer, fieldModels) => {
        for (const key in customer) {
            if (fieldModels[key]) {
                fieldModels[key].value.value = customer[key];
            }
        }
        fieldModels['address'].value.value = customer.adrdefault.address;
        fieldModels['city'].value.value = customer.adrdefault.city;
        fieldModels['state'].value.value =
            customer.adrdefault.city?.state?.name;
        fieldModels['country'].value.value =
            customer.adrdefault.city?.state?.country?.name;
        fieldModels['zip'].value.value = customer.adrdefault.zip;
        fieldModels['email'].value.value = customer.email;
        fieldModels['phone'].value.value = customer.adrdefault.phone;
        fieldModels['mobile'].value.value = customer.adrdefault.mobile;
        fieldModels['docid'].value.value = customer.adrdefault.docid;
    };

    const setFieldModelsAddressData = (address, fieldModels) => {
        for (const key in address) {
            if (fieldModels[key]) {
                fieldModels[key].value.value = address[key];
            }
        }

        fieldModels['address'].value.value = address.adrdefault.address;
        fieldModels['city'].value.value = address.adrdefault.city;
        fieldModels['state'].value.value = address.adrdefault.city?.state?.name;
        fieldModels['country'].value.value =
            address.adrdefault.city?.state?.country?.name;
        fieldModels['zip'].value.value = address.adrdefault.zip;
        fieldModels['emailAddresses'].value.value =
            address.adrdefault.emailAddresses;
        fieldModels['email'].value.value = address.email;
        fieldModels['phone'].value.value = address.adrdefault.phone;
        fieldModels['mobile'].value.value = address.adrdefault.mobile;
        fieldModels['docid'].value.value = address.adrdefault.docid;
    };

    return {
        loading,
        removeCustomer,
        saveCustomer,
        saveCustomerAddress,
        getCustomer,
        getCustomerAddress,
        getCustomerAddresses,
        listCustomerAddress,
        updateCustomerAddress,
        createCustomer,
        updateCustomer,
        createAddress,
        updateAddress,
        saveCustomerWithAddresses,
        setFieldModelsGeneralData,
        setFieldModelsAddressData
    };
}
